import { err, ok, type Result } from 'neverthrow';
import { z } from 'zod';

import { addCharsEveryK } from './addCharsEveryK.js';
import { naiveReverse } from './reverse.js';

import { Percent } from './percent.js';

/** Display precision for `Amount.display` / `AbsoluteAmount.display`. */
export type AmountDisplayFormat = 'cents' | 'rounded' | 'thousands';

const amountRegex = /^-?[0-9]+(\.[0-9]{1,3})?$/;
const whitespaceRegex = /\s/g;
const lettersRegex = /\p{L}/gu;

/**
 * Minor-unit scale. The value is stored in **thousandths (mills)**, matching the
 * Rust `df_units::Amount` `i64` (`DECIMAL_MULTIPLIER = 1000`), so the eventual
 * trusted-edge cast (Rust emits its integer) is a true zero-cost `as`. `cents`
 * (`fromCents`/`toCents`) is a documented bridge to the WASM `{ cents }` wire.
 */
const MILLS_PER_UNIT = 1000;
const MILLS_PER_CENT = 10;

declare const amountBrand: unique symbol;
declare const absoluteAmountBrand: unique symbol;

/**
 * Branded number type for a monetary amount, in integer **thousandths (mills)**,
 * matching the backend `df_units::Amount` unit.
 *
 * At runtime an `Amount` IS its mill count, so creating one is a zero-cost cast
 * and it is clone-safe across a Web Worker boundary. Operations live on the
 * `Amount` namespace value below. All amounts are SEK; currency is not modeled.
 */
export type Amount = number & { readonly [amountBrand]: 'Amount' };

/**
 * A non-negative {@link Amount}. A subtype of `Amount` (it carries both brands),
 * so it is assignable to `Amount` and every `Amount.*` operation works on it
 * directly — no parallel namespace. Produced by `Amount.absolute` or
 * `AbsoluteAmount.zero`.
 */
export type AbsoluteAmount = Amount & { readonly [absoluteAmountBrand]: 'AbsoluteAmount' };

// ----- Shared numeric core (operates on raw mills; the namespaces are typed facades) -----

function millsFromWhole(value: number): number {
  return Math.round(value * MILLS_PER_UNIT);
}

function millsFromCents(cents: number): number {
  return Math.round(cents * MILLS_PER_CENT);
}

/** Lenient user-input parse: strip letters/whitespace/symbols, accept a `,` decimal, `0` on failure. */
function millsFromUserInput(input: string): number {
  const normalized = input
    .replace(lettersRegex, '')
    .replace(whitespaceRegex, '')
    .replace(/,/g, '.');
  const value = parseFloat(normalized);
  return isNaN(value) ? 0 : Math.round(value * MILLS_PER_UNIT);
}

/** Strict string parse (trims, accepts a `,` decimal and spaces). */
function millsParse(s: string): Result<number, string> {
  const preProcessed = s.trim().replace(',', '.').replace(whitespaceRegex, '');
  if (!amountRegex.test(preProcessed)) {
    return err(`String to parse as amount contained unexpected characters: "${preProcessed}"`);
  }
  const value = parseFloat(preProcessed);
  if (isNaN(value)) {
    return err(`Could not parse "${s}" as a number`);
  }
  return ok(Math.round(value * MILLS_PER_UNIT));
}

function millsToCents(mills: number): number {
  return Math.round(mills / MILLS_PER_CENT);
}

function millsRound(mills: number): number {
  return Math.round(mills / MILLS_PER_UNIT) * MILLS_PER_UNIT;
}

function millsCeil(mills: number): number {
  return Math.ceil(mills / MILLS_PER_UNIT) * MILLS_PER_UNIT;
}

function millsFloor(mills: number): number {
  return Math.floor(mills / MILLS_PER_UNIT) * MILLS_PER_UNIT;
}

function compareNumbers(a: number, b: number): number {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
}

function sumMills<T>(
  items: readonly T[],
  transform: ((item: T) => number | null | undefined) | undefined,
): number {
  return items.reduce<number>((total, item) => {
    const value = transform ? transform(item) : (item as number | null | undefined);
    return value === null || value === undefined ? total : total + value;
  }, 0);
}

/** `d+.dd` machine string (dot decimal, no grouping) — the outbound DTO / internal format. */
function toDtoString(mills: number): string {
  return (mills / MILLS_PER_UNIT).toFixed(2);
}

function displayCurrency(mills: number, format: AmountDisplayFormat): string {
  const scaled = format === 'thousands' ? mills / MILLS_PER_UNIT : mills;
  const rounded = format !== 'cents';
  const amount = scaled / MILLS_PER_UNIT;

  const sekString = rounded ? Math.round(amount).toString() : amount.toFixed(2);
  const isNegative = (rounded ? Math.round(amount) : amount) < 0;
  const sekStringWithoutSign = isNegative ? sekString.slice(1) : sekString;
  const parts = sekStringWithoutSign.split('.');
  const whole = parts[0];
  const partial = parts[1];

  const sign = isNegative ? '-' : '';
  const wholePartFormatted = naiveReverse(addCharsEveryK(naiveReverse(whole), ' ', 3));
  return `${sign}${wholePartFormatted}${rounded ? '' : ',' + partial}`;
}

export const Amount = {
  // ----- Smart constructors -----

  /** Cast a raw mills (thousandths) integer to `Amount` — the trusted-boundary path (matches Rust). */
  unsafeFromMills(mills: number): Amount {
    return mills as Amount;
  },

  /** From a cent count (100 per whole unit). */
  fromCents(cents: number): Amount {
    return millsFromCents(cents) as Amount;
  },

  /** From a number of whole currency units (e.g. `100.5`). */
  fromWhole(value: number): Amount {
    return millsFromWhole(value) as Amount;
  },

  zero(): Amount {
    return 0 as Amount;
  },

  /** Strict string parse; `Result<Amount, string>`. */
  parse(s: string): Result<Amount, string> {
    return millsParse(s).map((mills) => mills as Amount);
  },

  /** Lenient free-form text-field parse; `zero` on failure. */
  fromUserInput(input: string): Amount {
    return millsFromUserInput(input) as Amount;
  },

  // ----- Accessors -----

  /** Raw mills — the exact underlying integer. */
  toMills(value: Amount): number {
    return value;
  },

  /** Cent count (rounded); lossy for sub-cent values, like the Rust `cents()`. */
  toCents(value: Amount): number {
    return millsToCents(value);
  },

  /** Whole currency units as a plain number (e.g. `100.5`); the inverse of `fromWhole`. */
  toWhole(value: Amount): number {
    return value / MILLS_PER_UNIT;
  },

  // ----- Predicates -----

  isZero(value: Amount): boolean {
    return value === 0;
  },

  isDebit(value: Amount): boolean {
    return value > 0;
  },

  // ----- Comparisons -----

  /** Ascending comparison (for sort callbacks; use `<`/`>`/`===` directly otherwise). */
  compare(a: Amount, b: Amount): number {
    return compareNumbers(a, b);
  },

  // ----- Arithmetic -----

  add(a: Amount, b: Amount): Amount {
    return (a + b) as Amount;
  },

  subtract(a: Amount, b: Amount): Amount {
    return (a - b) as Amount;
  },

  multiply(value: Amount, percent: Percent): Amount {
    return Math.round(value * Percent.fraction(percent)) as Amount;
  },

  /**
   * Dimensionless quotient `numerator / denominator` (the mill scale cancels).
   * Wrap in `Percent.fromFraction` for a percentage. The caller must guard
   * against a zero denominator (returns `Infinity`/`NaN` otherwise).
   */
  ratio(numerator: Amount, denominator: Amount): number {
    return numerator / denominator;
  },

  negate(value: Amount): Amount {
    return -value as Amount;
  },

  round(value: Amount): Amount {
    return millsRound(value) as Amount;
  },

  ceil(value: Amount): Amount {
    return millsCeil(value) as Amount;
  },

  floor(value: Amount): Amount {
    return millsFloor(value) as Amount;
  },

  absolute(value: Amount): AbsoluteAmount {
    return Math.abs(value) as unknown as AbsoluteAmount;
  },

  /**
   * Sum a list of amounts, skipping `null`/`undefined`. Pass a `transform` to sum
   * a field of each item (`Amount.sum(rows, (row) => row.balance)`) instead of
   * mapping first; the transform may also return `null`/`undefined` to skip.
   */
  sum<T = Amount | null | undefined>(
    items: readonly T[],
    transform?: (item: T) => Amount | null | undefined,
  ): Amount {
    return sumMills(items, transform) as Amount;
  },

  // ----- Display -----

  /**
   * SEK-formatted string. `format`: `'cents'` (default, two decimals),
   * `'rounded'` (whole kronor), `'thousands'` (whole thousands of kronor).
   */
  display(value: Amount, format: AmountDisplayFormat = 'cents'): string {
    return displayCurrency(value, format);
  },

  /** Displays the magnitude (`|value|`) at cents precision. */
  displayAbsolute(value: Amount): string {
    return displayCurrency(Math.abs(value), 'cents');
  },

  /** `d+.dd` machine string (dot decimal, no grouping) for request bodies. */
  toDto(value: Amount): string {
    return toDtoString(value);
  },

  // ----- Coders (cf. simple_id.ts) -----

  /** Zod schema for the decimal-string wire form; parses and brands. */
  schema: z.string().transform((value, ctx): Amount => {
    // The wire form (see toDto) is already a normalized `d+.dd` string, so validate it directly
    // rather than routing through Amount.parse's trim/comma/whitespace normalization and neverthrow
    // Result. This runs once per amount — hundreds of thousands of times on a large search — so it
    // avoids those allocations and the extra regex pass. A value matching `amountRegex` always
    // `parseFloat`s to a finite number, so no further NaN check is needed.
    if (!amountRegex.test(value)) {
      ctx.addIssue({
        code: 'custom',
        message: `String to parse as amount contained unexpected characters: "${value}"`,
      });
      return z.NEVER;
    }
    return Math.round(parseFloat(value) * MILLS_PER_UNIT) as Amount;
  }),

  /**
   * Zod schema for a future integer-mills wire form (ADR-0011 trusted boundary).
   * Not wired up until Rust emits its integer; kept so that switch is localized.
   */
  trustedSchema: z.number().transform((value): Amount => Amount.unsafeFromMills(value)),
};

/**
 * `AbsoluteAmount` is a subtype of `Amount` (see the type above), so read,
 * compare, arithmetic and display all go through the `Amount` namespace. Only
 * the non-negative constructors live here; `Amount.absolute` is the other
 * producer.
 */
export const AbsoluteAmount = {
  zero(): AbsoluteAmount {
    return 0 as AbsoluteAmount;
  },

  /** Zod schema for the decimal-string wire form; parses via `Amount.schema` and takes the absolute value. */
  schema: Amount.schema.transform((value): AbsoluteAmount => Amount.absolute(value)),
};
